import { PkIdentifier } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import type { ComponentProps, HTMLAttributeAnchorTarget, MouseEvent, ReactNode } from 'react';
import { Fragment, memo, useCallback, useMemo, useRef } from 'react';

import TeamBadge from '../TeamBadge';

import { getTeamUrl } from '@services/LinkRuleService';
import { hydrateWithTranslations } from '@services/TranslatorService';
import { useFitty } from '@shared/hooks/useFitty';
import type { RoundPhase, Team } from '@shared-types/match.types';
import { TeamType } from '@shared-types/match.types';
import { isNational, isRegions } from '@utilities/live';

export type TeamIdentifierProps = ComponentProps<typeof PkIdentifier> & {
  badgeClassName?: string;
  team?: Team;
  displayOrder?: 'inverted' | 'normal';
  showTeamName?: boolean;
  showTeamCode?: boolean;
  showTeamCountry?: boolean;
  badgeSize?: number;
  badgeSlot?: 'prefix' | 'suffix';
  customSecondaryText?: string | ReactNode;
  backgroundColor?: string;
  rounded?: boolean;
  borderRadius?: string;
  competitionId?: string;
  phase?: RoundPhase;
  customPlaceholder?: string;
  onIdentifierSelected?: (
    event: CustomEvent<{
      element: HTMLElement;
      selected: boolean;
    }>,
  ) => void;
  enableLink?: boolean;
  linkTarget?: HTMLAttributeAnchorTarget;
  linkSubPage?: string;
  isHistorical?: boolean;
  wrapperClass?: string;
  enableFitty?: boolean;
  minSize?: number;
  onLinkClick?: (e: MouseEvent) => void;
};

const TeamIdentifier = (props: TeamIdentifierProps) => {
  const {
    className = 'pk-py--xs',
    badgeClassName,
    team,
    type = 'horizontal',
    displayOrder = 'normal',
    showTeamName = true,
    showTeamCode,
    showTeamCountry = true,
    badgeSize = 48,
    badgeSlot = 'prefix',
    customSecondaryText,
    backgroundColor,
    rounded = false,
    competitionId = window.competitionId,
    phase = window.currentPhase,
    customPlaceholder,
    onIdentifierSelected,
    enableLink,
    linkTarget = '_self',
    onLinkClick,
    linkSubPage = '',
    isHistorical,
    wrapperClass,
    enableFitty = false,
    children,
    minSize = 8,
  } = props;
  const isTeam =
    isNational(team) ||
    isRegions(competitionId) ||
    (team?.typeTeam?.toUpperCase() === TeamType.PLACEHOLDER && !window.isClub);

  const nameField = showTeamCode ? 'displayTeamCode' : 'displayName';
  const primaryText = useMemo(() => hydrateWithTranslations(team)?.[nameField] ?? '', [nameField, team]);
  const secondaryText = useMemo(() => {
    if (customSecondaryText) return customSecondaryText;

    if (showTeamCountry && !isTeam) return hydrateWithTranslations(team)?.countryName ?? '';

    return '';
  }, [customSecondaryText, isTeam, showTeamCountry, team]);

  const handleLinkClick = useCallback(
    (e: MouseEvent) => {
      if (e.button > 1) return; // ignore right-clicks (context menu)
      onLinkClick?.(e);
    },
    [onLinkClick],
  );

  const teamNameRef = useRef<HTMLDivElement>(null);
  useFitty(teamNameRef, { minSize: minSize, multiLine: false, textOverflow: 'ellipsis' });

  const link = useMemo(() => {
    if (!enableLink || !team?.id || team?.typeTeam === TeamType.PLACEHOLDER || team?.idProvider !== 'FAME') return null;
    return getTeamUrl(team.id, competitionId, isHistorical, phase, linkSubPage);
  }, [competitionId, enableLink, isHistorical, linkSubPage, phase, team?.id, team?.idProvider, team?.typeTeam]);

  const hasLink = link && link !== '#';
  const Tag = hasLink ? 'a' : Fragment;

  const linkProps = useMemo(
    () =>
      hasLink
        ? {
            className: wrapperClass,
            title: hydrateWithTranslations(team)?.displayName,
            href: link,
            target: linkTarget,
            onClick: handleLinkClick,
            onAuxClick: handleLinkClick,
          }
        : {},
    [handleLinkClick, hasLink, link, linkTarget, team, wrapperClass],
  );

  return (
    <Tag {...linkProps}>
      <PkIdentifier
        className={clsx('pk-identifier', className)}
        onPkChange={onIdentifierSelected}
        type={type}
        order={displayOrder}
        rounded={rounded}
        bgColor={backgroundColor}
        {...props}
      >
        <TeamBadge
          className={badgeClassName}
          team={team}
          size={badgeSize}
          slot={badgeSlot}
          competitionId={competitionId}
          phase={phase}
          placeholder={customPlaceholder}
        />
        {showTeamName && (
          <div slot='primary'>
            <span ref={enableFitty ? teamNameRef : null}>{primaryText}</span>
          </div>
        )}
        {!!secondaryText && <span slot='secondary'>{secondaryText}</span>}
        {children}
      </PkIdentifier>
    </Tag>
  );
};

export default memo(TeamIdentifier);
